import { t } from 'i18next';

import { OpRawMenuItemType } from 'config/createRawMenuItems';
import { HomeOutlinedSvg } from 'components/svgs/HomeOutlinedSvg/HomeOutlinedSvg';
import { HomeFilledSvg } from 'components/svgs/HomeFilledSvg/HomeFilledSvg';
import { UserGroupFilledSvg } from 'components/svgs/UserGroupFilledSvg/UserGroupFilledSvg';
import { UserGroupOutlineSvg } from 'components/svgs/UserGroupOutlineSvg/UserGroupOutlineSvg';
import { ProductAccessFilledSvg } from 'components/svgs/ProductAccessFilledSvg/ProductAccessFilledSvg';
import { ProductAccessOutlineSvg } from 'components/svgs/ProductAccessOutlineSvg/ProductAccessOutlineSvg';
import { ProductProtectFilledSvg } from 'components/svgs/ProductProtectFilledSvg/ProductProtectFilledSvg';
import { ProductProtectOutlineSvg } from 'components/svgs/ProductProtectOutlineSvg/ProductProtectOutlineSvg';
import { ProductMailroomFilledSvg } from 'components/svgs/ProductMailroomFilledSvg/ProductMailroomFilledSvg';
import { ProductMailroomOutlineSvg } from 'components/svgs/ProductMailroomOutlineSvg/ProductMailroomOutlineSvg';
import { ProductVideoOutlineSvg } from 'components/svgs/ProductVideoOutlineSvg/ProductVideoOutlineSvg';
import { ProductVideoFilledSvg } from 'components/svgs/ProductVideoFilledSvg/ProductVideoFilledSvg';
import { BullhornOutlineSvg } from 'components/svgs/BullhornOutlineSvg/BullhornOutlineSvg';
import { TrainingFilledSvg } from 'components/svgs/TrainingFilledSvg/TrainingFilledSvg';
import { UserOutlinedSvg } from 'components/svgs/UserOutlinedSvg/UserOutlinedSvg';
import { UserFilledSvg } from 'components/svgs/UserFilledSvg/UserFilledSvg';
import { ProductVisitorFilledSvg } from 'components/svgs/ProductVisitorFilledSvg/ProductVisitorFilledSvg';
import { ProductVisitorOutlinedSvg } from 'components/svgs/ProductVisitorOutlinedSvg/ProductVisitorOutlinedSvg';

import {
  partnerDashboardRoute,
  partnerStoreRoute,
  partnerSubscriptionRenewalsTableRoute,
  partnerRequestsTableRoute,
  partnerProtectRoute,
  partnerProtectDeploymentsRoute,
  partnerMailroomRoute,
  partnerVideoRoute,
  partnerBillingRoute,
  partnerCustomersRoute,
  partnerVideoDeploymentsRoute,
  partnerAccessRoute,
  partnerUsersRoute,
  partnerBrandingRoute,
  partnerVisitorRoute,
} from './routes';

export const homeMenuItem = (): OpRawMenuItemType => ({
  key: partnerDashboardRoute,
  title: t('Home'),
  route: partnerDashboardRoute,
  icon: <HomeOutlinedSvg />,
  selectedIcon: <HomeFilledSvg />,
  scope: [
    'o:w',
    'o:r',
    's-o:r',
    's-o:w',
    's-o-video:w',
    's-o-video:r',
    'o{orgId}-admin:r',
    'o{orgId}-admin:w',
    'o{orgId}-erpParentStore:w',
    'o{orgId}-erpParentCenter:w',
  ],
});

export const brandingMenuItem = (): OpRawMenuItemType => ({
  key: partnerBrandingRoute,
  title: t('Branding'),
  route: partnerBrandingRoute,
  scope: ['s-o-video:w', 's-o-video:r', 's-o:w', 's-o:r', 'o:w', 'o:r'],
});

export const customersMenuItem = (): OpRawMenuItemType => ({
  key: partnerCustomersRoute,
  title: t('Customers'),
  route: partnerCustomersRoute,
  scope: ['s-o-video:w', 's-o-video:r', 's-o:w', 's-o:r', 'o:w', 'o:r'],
});

export const parentCustomersMenuItem = (
  isVideoPartner: boolean,
): OpRawMenuItemType => {
  if (!isVideoPartner) {
    return {
      ...customersMenuItem(),
      selectedIcon: <UserGroupFilledSvg />,
      icon: <UserGroupOutlineSvg />,
    };
  }

  return {
    key: `nav-${partnerCustomersRoute}`,
    title: t('Customers'),
    selectedIcon: <UserGroupFilledSvg />,
    icon: <UserGroupOutlineSvg />,
    scope: ['s-o-video:w', 's-o-video:r', 's-o:w', 's-o:r', 'o:w', 'o:r'],
    items: [customersMenuItem(), brandingMenuItem()],
  };
};

export const usersMenuItem = (): OpRawMenuItemType => ({
  key: partnerUsersRoute,
  title: t('Users'),
  route: partnerUsersRoute,
  icon: <UserOutlinedSvg />,
  selectedIcon: <UserFilledSvg />,
  scope: [
    's-o-video:w',
    's-o-video:r',
    's-o:w',
    's-o:r',
    'o:w',
    'o:r',
    'o{orgId}-user:r',
    'o{orgId}-user:w',
    'o-user:r',
    'o-user:w',
  ],
});

export const renewalsMenuItem = (): OpRawMenuItemType => ({
  key: partnerSubscriptionRenewalsTableRoute,
  title: t('Renewals'),
  route: partnerSubscriptionRenewalsTableRoute,
  scope: ['o{orgId}-erpParentStore:w', 'o:w', 'o:r'],
});

export const requestsMenuItem = (): OpRawMenuItemType => ({
  key: partnerRequestsTableRoute,
  title: t('Requests'),
  route: partnerRequestsTableRoute,
  scope: ['o:w', 'o:r', 's-o:r', 's-o:w'],
});

export const billingMenuItem = (): OpRawMenuItemType => ({
  key: partnerBillingRoute,
  title: t('Billing'),
  route: partnerBillingRoute,
  scope: ['o{orgId}-erpParentCenter:w', 'o:w', 'o:r'],
});

export const storeMenuItem = (): OpRawMenuItemType => ({
  key: partnerStoreRoute,
  title: t('Store'),
  route: partnerStoreRoute,
  scope: ['o{orgId}-erpParentStore:w', 'o:w', 'o:r'],
});

export const accessMenuItem = (isAccessPartner: boolean): OpRawMenuItemType => {
  if (!isAccessPartner) {
    return {
      key: partnerAccessRoute,
      title: t('Access'),
      route: partnerAccessRoute,
      icon: <ProductAccessOutlineSvg />,
      selectedIcon: <ProductAccessFilledSvg />,
      scope: ['s-o-video:w', 's-o-video:r', 'o:w', 'o:r'],
    };
  }

  return {
    key: 'nav-access',
    title: t('Access'),
    icon: <ProductAccessOutlineSvg />,
    selectedIcon: <ProductAccessFilledSvg />,
    scope: [
      'o{orgId}-erpParentCenter:w',
      'o{orgId}-erpParentStore:w',
      'o:w',
      'o:r',
      's-o:r',
      's-o:w',
    ],
    items: [
      renewalsMenuItem(),
      requestsMenuItem(),
      billingMenuItem(),
      storeMenuItem(),
    ],
  };
};

export const protectDeploymentsMenuItem = (): OpRawMenuItemType => ({
  key: partnerProtectDeploymentsRoute,
  title: t('Protect'),
  route: partnerProtectDeploymentsRoute,
  icon: <ProductProtectOutlineSvg />,
  selectedIcon: <ProductProtectFilledSvg />,
  scope: ['s-o-video:w', 's-o-video:r', 'o:w', 'o:r'],
});

// For now if you're a video partner you're a protect partner too
export const protectMenuItem = (isVideoPartner: boolean): OpRawMenuItemType => {
  if (!isVideoPartner) {
    return {
      key: partnerProtectRoute,
      title: t('Protect'),
      route: partnerProtectRoute,
      icon: <ProductProtectOutlineSvg />,
      selectedIcon: <ProductProtectFilledSvg />,
      scope: ['s-o:w', 's-o:r', 'o:w', 'o:r'],
    };
  }

  return protectDeploymentsMenuItem();
};

export const mailroomMenuItem = (): OpRawMenuItemType => ({
  key: partnerMailroomRoute,
  title: t('Mailroom'),
  route: partnerMailroomRoute,
  icon: <ProductMailroomOutlineSvg />,
  selectedIcon: <ProductMailroomFilledSvg />,
  /**
   * once mailroom is released we should remove the video scopes
   * since it is directly tied to access. Or we end up keeping the blurb
   * page and having a "real" page when we do have mailroom capabilities.
   * Reference Access or Video as examples
   */
  scope: ['s-o-video:w', 's-o-video:r', 's-o:w', 's-o:r', 'o:w', 'o:r'],
});

export const videoDeploymentsMenuItem = (): OpRawMenuItemType => ({
  key: partnerVideoDeploymentsRoute,
  title: t('Deployments'),
  route: partnerVideoDeploymentsRoute,
  scope: ['s-o-video:w', 's-o-video:r', 'o:w', 'o:r'],
});

export const videoMenuItem = (isVideoPartner: boolean): OpRawMenuItemType => {
  if (!isVideoPartner) {
    return {
      key: partnerVideoRoute,
      title: t('Video'),
      route: partnerVideoRoute,
      icon: <ProductVideoOutlineSvg />,
      selectedIcon: <ProductVideoFilledSvg />,
      scope: ['s-o:w', 's-o:r', 'o:w', 'o:r'],
    };
  }

  // For now theres only 1 video item so lets not have a drawer
  // When we want to have a drawer change to the following

  // {
  //   key: 'nav-video',
  //   title: t('Video'),
  //   icon: <ProductVideoOutlineSvg />,
  //   selectedIcon: <ProductVideoFilledSvg />,
  //   scope: ['s-o-video:w', 's-o-video:r', 'o:w', 'o:r'],
  //   items: [videoDeploymentsMenuItem()],
  // };

  return {
    ...videoDeploymentsMenuItem(),
    title: t('Video'),
    icon: <ProductVideoOutlineSvg />,
    selectedIcon: <ProductVideoFilledSvg />,
  };
};

export const visitorMenuItem = (): OpRawMenuItemType => {
  return {
    key: partnerVisitorRoute,
    title: t('Visitor'),
    route: partnerVisitorRoute,
    icon: <ProductVisitorOutlinedSvg />,
    selectedIcon: <ProductVisitorFilledSvg />,
    scope: ['s-o-video:w', 's-o-video:r', 's-o:w', 's-o:r', 'o:w', 'o:r'],
  };
};

export const marketingMenuItem = (): OpRawMenuItemType => ({
  key: 'nav-marketing',
  title: t('Marketing'),
  icon: <BullhornOutlineSvg />,
  route: 'https://vsapartners.motorolasolutions.com/s/',
  scope: ['o:r', 'o:w', 's-o:r', 's-o:w'],
  external: true,
});

export const trainingMenuItem = (): OpRawMenuItemType => ({
  key: 'nav-training',
  title: t('Training'),
  icon: <TrainingFilledSvg />,
  route: 'https://learningcenter.motorolasolutions.com/',
  scope: ['o:r', 'o:w', 's-o:r', 's-o:w'],
  external: true,
});

interface SidebarItemsArgs {
  isVideoPartner: boolean;
  isAccessPartner: boolean;
  isPartnerBrandingEnabled?: boolean;
}

export const sidebarItems = ({
  isVideoPartner,
  isAccessPartner,
}: SidebarItemsArgs): OpRawMenuItemType[] => [
  homeMenuItem(),
  parentCustomersMenuItem(isVideoPartner),
  usersMenuItem(),
  accessMenuItem(isAccessPartner),
  protectMenuItem(isVideoPartner),
  mailroomMenuItem(),
  videoMenuItem(isVideoPartner),
  visitorMenuItem(),
  marketingMenuItem(),
  trainingMenuItem(),
];
